<template>
    <form-modal
        id="dataModal"
        ref="modal"
        :title="exportTitle"
        :in-editing="(step !== 0 && step !== 1)"
        :size="sizeModal"
        :handle-hide="cleanModal"
        :handle-open="resetValidation"
    >
        <template v-slot:form>
            <confirmation-form-modal
                ref="confirmationFormModal"
                :title="i18nType"
                :operation="'export'"
            />
            <div>
                <b-form-group
                    :label="i18nExportType"
                    class="required label-pdv"
                    label-for="modal-type"
                    :invalid-feedback="getI18n('ERROR_CODES.invalid_type')"
                >
                    <translatedMultiselect
                        v-model="$v.export.type.$model"
                        :options="exportTypesFiltered"
                        @blur="$v.export.type.$touch"
                    />
                </b-form-group>
            </div>
        </template>
        <template v-slot:footer>
            <div>
                <cancel-button
                    ref="cancelButton"
                    child-class="col-sm-4 float-left"
                    @onClick="handleCancel"
                />
                <save-button
                    ref="okButton"
                    child-class="col-sm-4 float-right ml-3"
                    :title="getI18n('COMMON.export')"
                    @onClick="handleOk"
                />
            </div>
        </template>
    </form-modal>
</template>

<script>
    import CancelButton from '@/src/components/CancelButton'
import SaveButton from '@/src/components/SaveButton'
import FormModal from '@components/FormModal'
import translatedMultiselect from '@components/translatedMultiselect'
import ConfirmationFormModal from '@src/components/ConfirmationFormModal'
import validation from '@src/mixins/validation'
import ExportJobService from '@src/services/ExportJobService'
import { minLength, required } from 'vuelidate/lib/validators'

    const i18nCommon = 'COMMON'
    const i18nKey = 'EXPORT_JOBS'

    export default {
        components: {
            ConfirmationFormModal,
            translatedMultiselect,
            FormModal,
            CancelButton,
            SaveButton,
        },
        mixins: [
            validation,
        ],
        props: {
            type: {
                type: String,
                required: true,
            },
            availableTypes: {
                type: Array,
                required: false,
                default: () => [],
            },
            title: {
                type: String,
                default: null,
            },
            createParameters: {
                type: Function,
                required: true,
            },
        },
        data() {
            return {
                i18nCommon,
                i18nKey,
                step: 1,
                size: 'md',
                loading: false,
                export: {
                    type: null
                },
                exportTypes: [
                    {
                        value: 'mix',
                        text: this.getI18n('MIXES', 'TITLES.mix'),
                    },
                    {
                        value: 'mix_has_product',
                        text: this.getI18n('MIXES', 'TITLES.mix_has_product'),
                    },
                    {
                        value: 'team',
                        text: this.getI18n('TEAMS', 'TITLES.team'),
                    },
                ]
            }
        },
        validations: {
            export: {
                type: {
                    required,
                    minLength: minLength(1),
                },
            }
        },
        computed: {
            exportTitle() {
                return `${this.getI18n(i18nKey, 'TITLES.export')}`
            },
            i18nType() {
                return `${this.getI18n(i18nKey, 'TITLES.export')}${this.export.type ? `: ${this.export.type.text}` : ''}`
            },
            i18nExportType() {
                return this.getI18n(i18nKey, 'TITLES.export_type')
            },
            sizeModal() {
                if (this.step === 0 || this.step === 1) {
                    return 'md'
                }
                return 'xl'
            },
            exportTypesFiltered() {
                return this.availableTypes.length ? this.exportTypes.filter(type => this.availableTypes.includes(type.value)) : this.exportTypes
            },
        },
        methods: {
            showModal() {
                this.fieldsByType = null
                this.step = this.availableTypes.length > 0 ? 0 : 1
                if (this.type) {
                    this.export.type = this.type || null
                }
                this.cleanModal()
                this.$refs.modal.show()
            },

            hideModal() {
                this.$refs.modal.hide()
            },

            resetValidation() {
                this.$nextTick(() => this.$v.$reset())
                this.clearAllResponseError()
            },

            cleanModal() {
                this.loading = false
                Object.assign(this.export, {
                    type: null,
                })
                this.resetValidation()
            },

            handleCancel() {
                if (this.step > 1) {
                    this.$refs.confirmationFormModal.showModal(true, this.handleClose)
                }
                else {
                    this.handleClose(true)
                }
            },

            handleClose(close) {
                if (!close) {
                    return
                }
                this.export.type = null
                this.cleanModal()
                this.resetValidation()
                this.hideModal()
            },

            async handleOk() {
                this.$v.export.$touch()
                if (!this.loading) {
                    this.$refs.confirmationFormModal.showModal(false, this.handleSubmit)
                }
            },
            async handleSubmit(submit) {
                if (!submit) {
                    return
                }

                this.loading = true
                try {
                    let parameters = await this.createParameters()

                    parameters = {
                        ...parameters,
                        export_type: this.export.type.value,
                        language: 'pt_BR',
                    }
                    const response = await ExportJobService.create(
                        parameters
                    )
                        .then((response) => response)
                        .catch((error) => {
                            return error
                        })

                    const statusCode = ExportJobService.exportStatus(response)

                    if (statusCode.charAt(0) === '2') {
                        this.resetValidation()
                        this.hideModal()
                        this.$router.push({ name: 'exportJobs', params: { newJobCreated: true } })
                    }

                    if (statusCode === '417') {
                        const suffix = `ERRORS.${ExportJobService.exportErrorMessage(response)}`
                        this.operationUnsuccessful(i18nKey, suffix, 'create')
                    }

                    if (statusCode === '422') {
                        this.modalResponseErrors = response.data?.errors
                            ?? response.response?.data?.errors
                    }
                } finally {
                    this.loading = false
                }
            },
        },
    }
</script>
